// src/components/BriefingsPanel.jsx
//
// Surfaces the latest Portfolio Radar agent briefing at the top of the
// pipeline view. Member+ can generate a new briefing on demand. Brief
// renders four buckets: top headline, summary, focus picks, needs
// attention, what changed, notable prospects. Each deal-bound item is
// click-through to its deal.

import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2, AlertTriangle, Target, TrendingUp, Compass, ChevronDown, ChevronUp, RefreshCw, X } from 'lucide-react';
import { supabase } from '../lib/supabase';

const URGENCY_COLOR = {
  high:   { bg: '#FEE2E2', fg: '#B91C1C', dot: '#DC2626' },
  medium: { bg: '#FFEDD5', fg: '#9A3412', dot: '#F97316' },
  low:    { bg: '#FEF3C7', fg: '#92400E', dot: '#EAB308' },
};

export default function BriefingsPanel({ organizationId, canGenerate, onSelectDeal, darkMode }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('lfg.radar.collapsed') !== 'false'; }
    catch { return true; }
  });

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from('agent_briefings')
        .select('*')
        .eq('organization_id', organizationId)
        .order('generated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled) {
        setBriefing(data || null);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [organizationId]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-portfolio-radar`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );
      const result = await res.json();
      if (!res.ok || !result.ok) throw new Error(result.error || 'Generation failed');
      setBriefing(result.briefing);
      setCollapsed(false);
    } catch (e) {
      setError(e.message || 'Failed to generate briefing');
    } finally {
      setGenerating(false);
    }
  };

  const card = (children) => (
    <div
      className="rounded-2xl overflow-hidden relative"
      style={{
        backgroundColor: darkMode ? '#161b26' : '#ffffff',
        border: darkMode ? '1px solid #252b38' : '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={{ background: 'linear-gradient(90deg, #7C3AED 0%, #A78BFA 30%, #C4B5FD 70%, transparent 100%)' }}
      />
      {children}
    </div>
  );

  if (loading) {
    return card(
      <div className="px-5 py-4 flex items-center gap-2 text-sm" style={{ color: darkMode ? '#94a3b8' : '#64748B' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Loading portfolio briefing…
      </div>
    );
  }

  // Empty state — no briefing yet
  if (!briefing) {
    return card(
      <div className="px-5 py-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
               style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}>
            <Compass className="w-4.5 h-4.5 text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold" style={{ color: darkMode ? '#f1f5f9' : '#0F172A' }}>Portfolio Radar</div>
            <div className="text-xs" style={{ color: darkMode ? '#94a3b8' : '#64748B' }}>
              AI briefing across every deal — what changed, what needs attention, what to focus on this week.
            </div>
          </div>
        </div>
        {canGenerate && (
          <button
            onClick={generate}
            disabled={generating}
            className="h-9 inline-flex items-center gap-1.5 px-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating…' : 'Generate briefing'}
          </button>
        )}
        {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
      </div>
    );
  }

  const generatedAt = briefing.generated_at ? new Date(briefing.generated_at) : null;
  const dealLink = (deal_id, deal_name) => (
    <button
      onClick={() => onSelectDeal && onSelectDeal(deal_id)}
      className="font-semibold hover:underline text-left"
      style={{ color: darkMode ? '#93b8e8' : '#1A3665' }}
    >
      {deal_name}
    </button>
  );

  return card(
    <div>
      <div
        className="px-5 py-3 flex items-center gap-3 cursor-pointer"
        onClick={() => setCollapsed(c => { const next = !c; try { localStorage.setItem('lfg.radar.collapsed', String(next)); } catch {} return next; })}
        style={{ borderBottom: collapsed ? 'none' : (darkMode ? '1px solid #252b38' : '1px solid #F0F2F6') }}
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
             style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)' }}>
          <Compass className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#7C3AED' }}>Portfolio Radar</span>
            {generatedAt && (
              <span className="text-[11px]" style={{ color: darkMode ? '#94a3b8' : '#94A3B8' }}>
                · {generatedAt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            )}
            <span className="text-[11px]" style={{ color: darkMode ? '#94a3b8' : '#94A3B8' }}>
              · {briefing.deals_count} deals · {briefing.prospects_count} prospects
            </span>
          </div>
          {briefing.headline && (
            <div className="text-sm font-semibold mt-0.5 truncate" style={{ color: darkMode ? '#f1f5f9' : '#0F172A' }}>
              {briefing.headline}
            </div>
          )}
        </div>
        {canGenerate && (
          <button
            onClick={(e) => { e.stopPropagation(); generate(); }}
            disabled={generating}
            className="h-8 inline-flex items-center gap-1.5 px-2.5 text-xs font-semibold rounded-md transition-colors disabled:opacity-50"
            style={{
              backgroundColor: darkMode ? '#1c2230' : '#F5F3FF',
              color: '#7C3AED',
              border: darkMode ? '1px solid #2a3140' : '1px solid #DDD6FE',
            }}
            title="Re-generate the portfolio briefing"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {generating ? 'Generating…' : 'Re-run'}
          </button>
        )}
        {collapsed ? <ChevronDown className="w-4 h-4" style={{ color: darkMode ? '#94a3b8' : '#94A3B8' }} />
                   : <ChevronUp className="w-4 h-4" style={{ color: darkMode ? '#94a3b8' : '#94A3B8' }} />}
      </div>

      {!collapsed && (
        <div className="px-5 py-4 space-y-4">
          {briefing.summary && (
            <p className="text-sm leading-relaxed" style={{ color: darkMode ? '#e2e8f0' : '#334155' }}>
              {briefing.summary}
            </p>
          )}

          {(briefing.focus_picks || []).length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#7C3AED' }}>
                <Target className="w-3.5 h-3.5" /> Focus this week
              </div>
              <div className="space-y-1.5">
                {briefing.focus_picks.map((p, i) => (
                  <div key={i} className="text-sm flex gap-2" style={{ color: darkMode ? '#e2e8f0' : '#334155' }}>
                    <span className="text-purple-500 mt-0.5">•</span>
                    <div>
                      {dealLink(p.deal_id, p.deal_name)} <span className="text-xs" style={{ color: darkMode ? '#94a3b8' : '#64748B' }}>— {p.why}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(briefing.needs_attention || []).length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#DC2626' }}>
                <AlertTriangle className="w-3.5 h-3.5" /> Needs attention
              </div>
              <div className="space-y-1.5">
                {briefing.needs_attention.map((p, i) => {
                  const u = URGENCY_COLOR[p.urgency] || URGENCY_COLOR.low;
                  return (
                    <div key={i} className="text-sm flex gap-2 items-start" style={{ color: darkMode ? '#e2e8f0' : '#334155' }}>
                      <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: u.dot }} />
                      <div className="flex-1">
                        {dealLink(p.deal_id, p.deal_name)}
                        {p.urgency && (
                          <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase"
                                style={{ backgroundColor: u.bg, color: u.fg }}>
                            {p.urgency}
                          </span>
                        )}
                        <div className="text-xs mt-0.5" style={{ color: darkMode ? '#94a3b8' : '#64748B' }}>{p.why}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(briefing.what_changed || []).length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#0EA5E9' }}>
                <TrendingUp className="w-3.5 h-3.5" /> What changed
              </div>
              <div className="space-y-1.5">
                {briefing.what_changed.map((p, i) => (
                  <div key={i} className="text-sm flex gap-2" style={{ color: darkMode ? '#e2e8f0' : '#334155' }}>
                    <span className="text-sky-500 mt-0.5">•</span>
                    <div>
                      {dealLink(p.deal_id, p.deal_name)} <span style={{ color: darkMode ? '#cbd5e1' : '#475569' }}>— {p.headline}</span>
                      {p.detail && <div className="text-xs" style={{ color: darkMode ? '#94a3b8' : '#64748B' }}>{p.detail}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(briefing.notable_prospects || []).length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#10B981' }}>
                <Sparkles className="w-3.5 h-3.5" /> Notable new prospects
              </div>
              <div className="space-y-1.5">
                {briefing.notable_prospects.map((p, i) => (
                  <div key={i} className="text-sm flex gap-2" style={{ color: darkMode ? '#e2e8f0' : '#334155' }}>
                    <span className="text-emerald-500 mt-0.5">•</span>
                    <div>
                      <span className="font-semibold" style={{ color: darkMode ? '#f1f5f9' : '#0F172A' }}>{p.name}</span>
                      <span className="text-xs" style={{ color: darkMode ? '#94a3b8' : '#64748B' }}> — {p.why}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="text-xs text-red-500 flex items-center gap-1">
              <X className="w-3 h-3" /> {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
